import { useState, useCallback, useRef, useEffect } from 'react';
import { uploadVideo, UploadStatus } from '../libs/uploaders/uploadVideo';
import { DetailedError } from 'tus-js-client';
import { MAX_VIDEO_TITLE_LENGTH } from '../constants/MAX_VIDEO_TITLE_LENGTH';
import { Profile } from '../types/profiles/Profile';
import { vzhikLogger } from '../libs/vzhik-logger/VzhikLogger';
import { toastError, toastInfo } from '../libs/toasts/toasts';
import { toastsT } from '../i18n/toasts.i18n';

export type UploadFileItem = {
    id: string;
    file: File;
    title: string;
    profile: Profile['name'];
    enableSummarization: boolean;
    status: 'pending' | 'uploading' | 'success' | 'error';
    progress: number;
    error?: string;
};

type UploadPromise = {
    abort: () => Promise<void>;
    start: () => Promise<void>;
} | null;

type UseMultipleVideoUploadParams = {
    defaultProfile: string;
    onUploadSuccess?: () => void;
    onUploadStart?: (uploadUrl: string) => void;
};

export function useMultipleVideoUpload({
    defaultProfile,
    onUploadSuccess,
    onUploadStart,
}: UseMultipleVideoUploadParams) {
    const [files, setFiles] = useState<UploadFileItem[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [currentUploadIndex, setCurrentUploadIndex] = useState<number | null>(null);
    const shouldStopUploadRef = useRef(false);
    const uploadPromiseRef = useRef<UploadPromise>(null);
    const uploadStartedRef = useRef<Set<string>>(new Set());
    const uploadPromisesMapRef = useRef<Map<string, UploadPromise>>(new Map());
    const currentResolveMapRef = useRef<Map<string, (value: boolean) => void>>(new Map());

    const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (isUploading) {
                event.preventDefault();
                event.returnValue = true;
            }
        };

        if (isUploading) {
            window.addEventListener('beforeunload', handleBeforeUnload);
        }

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isUploading]);

    const addFiles = useCallback(
        (newFiles: File[]) => {
            const newItems: UploadFileItem[] = newFiles.map((file) => ({
                id: generateId(),
                file,
                title: file.name.slice(0, MAX_VIDEO_TITLE_LENGTH - 1),
                profile: defaultProfile,
                enableSummarization: false,
                status: 'pending',
                progress: 0,
            }));

            setFiles((prev) => [...prev, ...newItems]);
        },
        [defaultProfile],
    );

    const removeFile = useCallback((id: string) => {
        setFiles((prev) => prev.filter((item) => item.id !== id));
        uploadPromisesMapRef.current.delete(id);
        currentResolveMapRef.current.delete(id);
    }, []);

    const removeFileWithDelay = useCallback((id: string, delay: number) => {
        setTimeout(() => {
            setFiles((prev) => prev.filter((item) => item.id !== id));
        }, delay);
    }, []);

    const updateFile = useCallback(
        (id: string, updates: Partial<Omit<UploadFileItem, 'id' | 'file'>>) => {
            setFiles((prev) =>
                prev.map((item) => (item.id === id ? { ...item, ...updates } : item)),
            );
        },
        [],
    );

    const updateTitle = useCallback(
        (id: string, title: string) => {
            updateFile(id, { title });
        },
        [updateFile],
    );

    const updateProfile = useCallback(
        (id: string, profile: string) => {
            updateFile(id, { profile });
        },
        [updateFile],
    );

    const toggleSummarization = useCallback((id: string) => {
        setFiles((prev) =>
            prev.map((item) =>
                item.id === id ? { ...item, enableSummarization: !item.enableSummarization } : item,
            ),
        );
    }, []);

    const bulkUpdateProfile = useCallback((ids: string[], profile: string) => {
        setFiles((prev) =>
            prev.map((item) =>
                ids.includes(item.id) && item.status !== 'uploading' && item.status !== 'success'
                    ? { ...item, profile }
                    : item,
            ),
        );
    }, []);

    const bulkSetSummarization = useCallback((ids: string[], enabled: boolean) => {
        setFiles((prev) =>
            prev.map((item) =>
                ids.includes(item.id) && item.status !== 'uploading' && item.status !== 'success'
                    ? { ...item, enableSummarization: enabled }
                    : item,
            ),
        );
    }, []);

    const handleUploadStatusChange = useCallback(
        (
            id: string,
            props: {
                status?: UploadStatus;
                progress?: number;
                error?: Error | DetailedError;
            },
        ) => {
            switch (props.status) {
                case 'resume':
                    toastInfo.open(toastsT('resumeUpload'));
                    break;
                case 'error':
                    updateFile(id, {
                        status: 'error',
                        error: props.error?.message || toastsT('uploadFailed'),
                    });
                    uploadStartedRef.current.delete(id);
                    toastError.open(toastsT('uploadFailed'));
                    console.error('Не удалось загрузить видео по причине', props.error);
                    vzhikLogger.logError({
                        message: 'Не удалось загрузить видео (множественная загрузка)',
                        type: 'server',
                        additional: {
                            error: props.error,
                        },
                    });
                    break;
                case 'success':
                    updateFile(id, { status: 'success', progress: 100 });
                    uploadStartedRef.current.delete(id);
                    if (onUploadSuccess) {
                        onUploadSuccess();
                    }
                    removeFileWithDelay(id, 1500);
                    break;
                case 'progress':
                    updateFile(id, { progress: props.progress || 0 });
                    break;
                default:
                    break;
            }
        },
        [updateFile, onUploadSuccess, removeFileWithDelay],
    );

    const uploadSingleFile = useCallback(
        async (item: UploadFileItem): Promise<boolean> => {
            updateFile(item.id, { status: 'uploading' });

            return new Promise<boolean>(async (resolve) => {
                currentResolveMapRef.current.set(item.id, resolve);

                let uploadPromise = uploadPromisesMapRef.current.get(item.id);

                if (!uploadPromise) {
                    uploadPromise = await uploadVideo({
                        file: item.file,
                        videoTitle:
                            item.title || item.file.name.slice(0, MAX_VIDEO_TITLE_LENGTH - 1),
                        profile: item.profile,
                        enableSummarization: item.enableSummarization,
                        onUploadStateChange: (props) => {
                            handleUploadStatusChange(item.id, props);
                            if (props.status === 'success') {
                                uploadPromisesMapRef.current.delete(item.id);
                                currentResolveMapRef.current.delete(item.id);
                                resolve(true);
                            } else if (props.status === 'error') {
                                uploadPromisesMapRef.current.delete(item.id);
                                currentResolveMapRef.current.delete(item.id);
                                resolve(false);
                            }
                        },
                        onUploadStart,
                        onAbort: () => {
                            const currentResolve = currentResolveMapRef.current.get(item.id);
                            if (currentResolve) {
                                updateFile(item.id, { status: 'pending' });
                                uploadStartedRef.current.delete(item.id);
                                currentResolve(false);
                            }
                        },
                    });

                    if (uploadPromise) {
                        uploadPromisesMapRef.current.set(item.id, uploadPromise);
                    }
                }

                if (shouldStopUploadRef.current) {
                    updateFile(item.id, { status: 'pending' });
                    uploadStartedRef.current.delete(item.id);
                    currentResolveMapRef.current.delete(item.id);
                    resolve(false);
                    return;
                }

                uploadPromiseRef.current = uploadPromise;

                if (uploadPromise) {
                    uploadStartedRef.current.add(item.id);
                    await uploadPromise.start();
                } else {
                    updateFile(item.id, { status: 'error', error: toastsT('createUploadFailed') });
                    currentResolveMapRef.current.delete(item.id);
                    resolve(false);
                }
            });
        },
        [handleUploadStatusChange, updateFile, onUploadStart],
    );

    const startUploadAll = useCallback(async () => {
        const pendingFiles = files.filter((f) => f.status === 'pending' || f.status === 'error');
        if (pendingFiles.length === 0) {
            return;
        }

        setIsUploading(true);
        shouldStopUploadRef.current = false;

        for (let i = 0; i < pendingFiles.length; i++) {
            if (shouldStopUploadRef.current) {
                break;
            }

            const item = pendingFiles[i];
            setCurrentUploadIndex(files.findIndex((f) => f.id === item.id));

            await uploadSingleFile(item);
        }

        setIsUploading(false);
        setCurrentUploadIndex(null);
        shouldStopUploadRef.current = false;
        uploadPromiseRef.current = null;
    }, [files, uploadSingleFile]);

    const startUploadSingle = useCallback(
        async (id: string) => {
            const item = files.find((f) => f.id === id);
            if (!item || item.status === 'uploading') {
                return;
            }

            setIsUploading(true);
            setCurrentUploadIndex(files.findIndex((f) => f.id === id));

            await uploadSingleFile(item);

            setIsUploading(false);
            setCurrentUploadIndex(null);
            uploadPromiseRef.current = null;
        },
        [files, uploadSingleFile],
    );

    const stopUpload = useCallback(async () => {
        shouldStopUploadRef.current = true;

        if (uploadPromiseRef.current) {
            await uploadPromiseRef.current.abort();
        }
    }, []);

    const clearAll = useCallback(() => {
        if (!isUploading) {
            setFiles([]);
            uploadPromisesMapRef.current.clear();
            currentResolveMapRef.current.clear();
        }
    }, [isUploading]);

    const hasValidationErrors = useCallback(() => {
        return files.some(
            (item) =>
                !item.profile ||
                item.title.length > MAX_VIDEO_TITLE_LENGTH ||
                (!item.title && !item.file.name),
        );
    }, [files]);

    const pendingCount = files.filter((f) => f.status === 'pending' || f.status === 'error').length;
    const uploadingCount = files.filter((f) => f.status === 'uploading').length;
    const errorCount = files.filter((f) => f.status === 'error').length;

    return {
        files,
        isUploading,
        currentUploadIndex,
        addFiles,
        removeFile,
        updateTitle,
        updateProfile,
        toggleSummarization,
        bulkUpdateProfile,
        bulkSetSummarization,
        startUploadAll,
        startUploadSingle,
        stopUpload,
        clearAll,
        hasValidationErrors,
        pendingCount,
        uploadingCount,
        errorCount,
    };
}
